import { Component } from '@theme/component';

/**
 * Product gallery viewer: a full-screen dialog showing one image at a time, opened at the image
 * whose zoom button was pressed, with previous / next / close controls and arrow-key support.
 *
 * Below desktop the gallery is a slider (one image per slide on phones, two from 768px); this also builds its page dots.
 *
 * @extends {Component<{ dialog: HTMLDialogElement, slides: HTMLImageElement[], track: HTMLElement, dots: HTMLElement }>}
 */
class OudhZoom extends Component {
  #index = 0;

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;

  #visible = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #settleTimer;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog?.addEventListener('keydown', this.#onKeydown);
    this.refs.track?.addEventListener('scroll', this.#syncDots, { passive: true });
    this.#rebuild();
    window.matchMedia('(max-width: 767px)').addEventListener('change', this.#rebuild);

    // Autoplay stops for anything the visitor is doing to the gallery, and only runs on screen.
    for (const type of ['pointerenter', 'pointerdown', 'focusin']) this.addEventListener(type, this.#pause);
    for (const type of ['pointerleave', 'pointerup', 'pointercancel', 'focusout']) this.addEventListener(type, this.#play);
    this.refs.dialog?.addEventListener('close', this.#play);
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
    });
    this.#observer.observe(this);
    this.#play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('keydown', this.#onKeydown);
    this.refs.track?.removeEventListener('scroll', this.#syncDots);
    window.matchMedia('(max-width: 767px)').removeEventListener('change', this.#rebuild);
    this.#observer?.disconnect();
    this.#pause();
    clearTimeout(this.#settleTimer);
  }

  /**
   * @param {Event} event
   */
  open(event) {
    const trigger = /** @type {HTMLElement | null} */ (event.target);
    this.#show(Number(trigger?.dataset.index ?? 0));
    this.#pause();
    this.refs.dialog.showModal();
  }

  close() {
    this.refs.dialog.close();
  }

  previous() {
    this.#show(this.#index - 1);
  }

  next() {
    this.#show(this.#index + 1);
  }

  /**
   * A click on the bare dialog (outside the image and buttons) dismisses it.
   *
   * @param {Event} event
   */
  backdrop(event) {
    if (event.target === this.refs.dialog) this.close();
  }

  /**
   * @param {number} index - Wraps around at both ends.
   */
  #show(index) {
    const { slides } = this.refs;
    if (!slides?.length) return;

    this.#index = (index + slides.length) % slides.length;
    slides.forEach((slide, i) => {
      slide.hidden = i !== this.#index;
    });
  }

  /** Real (non-clone) frames of the slider. */
  #frames() {
    const { track } = this.refs;
    return /** @type {HTMLElement[]} */ ([...track.children].filter((child) => !child.hasAttribute('data-clone')));
  }

  /** Distance from one frame to the next, gap included. */
  #frameStep() {
    const { track } = this.refs;
    const [first, second] = /** @type {HTMLElement[]} */ ([...track.children]);
    return second ? second.offsetLeft - first.offsetLeft : track.clientWidth;
  }

  /** Images in view at once: 1 on phones, 2 above. Read from layout so CSS stays the only source. */
  #perSlide() {
    const { track } = this.refs;
    const first = /** @type {HTMLElement | undefined} */ (track.children[0]);
    if (!first) return 1;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return Math.max(1, Math.round((track.clientWidth + gap) / (first.offsetWidth + gap)));
  }

  /** Width of one full lap: from the first frame to its clone at the end. */
  #lap() {
    const { track } = this.refs;
    const clone = /** @type {HTMLElement | null} */ (track.querySelector('[data-clone]'));
    const first = /** @type {HTMLElement | undefined} */ (track.children[0]);
    return clone && first ? clone.offsetLeft - first.offsetLeft : 0;
  }

  /**
   * The slider loops forward like the hero slider. After the last frame it shows copies of the
   * first ones; once a scroll ends on a copy it jumps back to the real frame (identical to the eye).
   * Copies are inert: no zoom button, no wishlist mount, hidden from assistive tech.
   */
  #buildClones = () => {
    const { track } = this.refs;
    if (!track) return;

    for (const clone of track.querySelectorAll('[data-clone]')) clone.remove();

    const frames = this.#frames();
    if (frames.length < 2) return;

    for (const frame of frames.slice(0, this.#perSlide())) {
      const clone = /** @type {HTMLElement} */ (frame.cloneNode(true));
      clone.setAttribute('data-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.querySelectorAll('.oudh-pdp__zoom, .oudh-wls-mount').forEach((node) => node.remove());
      track.append(clone);
    }
  };

  /** One dot per slide; hidden by CSS when there is only one. Rebuilt when the layout changes. */
  #rebuild = () => {
    const { track, dots } = this.refs;
    if (!track || !dots) return;

    this.#buildClones();
    dots.replaceChildren();

    const pages = Math.ceil(this.#frames().length / this.#perSlide());
    if (pages < 2) return;

    for (let i = 0; i < pages; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'oudh-pdp__dot';
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => this.#goTo(i));
      dots.append(dot);
    }

    this.#syncDots();
  };

  #play = () => {
    if (this.dataset.autoplay !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#pause();
    this.#timer = setInterval(() => this.#advance(), Number(this.dataset.interval) || 6000);
  };

  #pause = () => {
    clearInterval(this.#timer);
    this.#timer = undefined;
  };

  /** Does nothing off screen, behind the viewer, or on desktop (no slider there). */
  #advance() {
    const { track, dialog } = this.refs;
    if (!track || !this.#visible || dialog?.open) return;
    if (track.scrollWidth - track.clientWidth <= 1) return;

    this.#step(1);
  }

  slidePrevious() {
    this.#step(-1);
  }

  slideNext() {
    this.#step(1);
  }

  /** @param {number} direction */
  #step(direction) {
    const { track } = this.refs;
    const lap = this.#lap();

    // Going back from the first slide: jump to the identical spot one lap along, then glide back.
    if (direction < 0 && lap && track.scrollLeft <= 1) this.#jump(lap);

    const distance = this.#perSlide() * this.#frameStep();
    const max = track.scrollWidth - track.clientWidth;
    track.scrollTo({ left: Math.min(track.scrollLeft + direction * distance, max), behavior: 'smooth' });
  }

  /** @param {number} page */
  #goTo(page) {
    const { track } = this.refs;
    track.scrollTo({ left: page * this.#perSlide() * this.#frameStep(), behavior: 'smooth' });
  }

  /**
   * Instant move with snapping off for a frame, so the browser does not snap it somewhere else.
   *
   * @param {number} left
   */
  #jump(left) {
    const { track } = this.refs;
    track.style.scrollSnapType = 'none';
    track.scrollLeft = left;
    requestAnimationFrame(() => {
      track.style.scrollSnapType = '';
    });
  }

  /** Once scrolling stops on a copy, return to the real frame it copies. */
  #settle = () => {
    const { track } = this.refs;
    const lap = this.#lap();
    if (lap && track.scrollLeft >= lap - 1) this.#jump(track.scrollLeft - lap);
  };

  #syncDots = () => {
    const { track, dots } = this.refs;
    if (!track || !dots) return;

    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(this.#settle, 120);

    const lap = this.#lap();
    const pages = dots.children.length;
    if (!lap || !pages) return;

    const position = ((track.scrollLeft % lap) + lap) % lap;
    const page = Math.min(pages - 1, Math.floor((position / lap) * pages + 0.25));
    [...dots.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === page)));
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'ArrowLeft') this.previous();
    if (event.key === 'ArrowRight') this.next();
  };
}

if (!customElements.get('oudh-zoom')) {
  customElements.define('oudh-zoom', OudhZoom);
}
